// Bookings are stored in UTC. Operations happen in Europe/Amsterdam.
// "Today" therefore means: today in Europe/Amsterdam, expressed as a UTC range.

export const VENUE_TZ = 'Europe/Amsterdam';

function tzOffsetMinutes(d: Date, tz: string): number {
  // Compute the offset for `d` in the named timezone by formatting it twice
  // and diffing the wall clocks. Avoids pulling in a tz lib.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const utcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') === 24 ? 0 : get('hour'),
    get('minute'),
    get('second'),
  );
  return (utcMs - d.getTime()) / 60000;
}

export function venueDayBounds(now: Date = new Date()): { startUtc: string; endUtc: string } {
  // Get year/month/day in Europe/Amsterdam.
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const ymd = dtf.format(now); // 'YYYY-MM-DD'
  const [y, m, d] = ymd.split('-').map(Number);

  // Build midnight-in-venue-tz as a UTC instant.
  // 1) Pretend midnight is UTC; 2) subtract the venue offset for that wall time.
  const tentative = new Date(Date.UTC(y!, (m! - 1), d!, 0, 0, 0));
  const offset = tzOffsetMinutes(tentative, VENUE_TZ);
  const startUtcMs = tentative.getTime() - offset * 60000;

  // End-of-day = start + 24h; recompute offset in case of DST flip in between.
  const tentativeEnd = new Date(Date.UTC(y!, (m! - 1), d! + 1, 0, 0, 0));
  const offsetEnd = tzOffsetMinutes(tentativeEnd, VENUE_TZ);
  const endUtcMs = tentativeEnd.getTime() - offsetEnd * 60000;

  return {
    startUtc: new Date(startUtcMs).toISOString(),
    endUtc: new Date(endUtcMs).toISOString(),
  };
}

// 'YYYY-MM' of `now` in the venue timezone (the <input type="month"> value).
export function venueYearMonth(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
  })
    .format(now)
    .slice(0, 7);
}

// [start, end) of a venue-local calendar month as UTC instants. `ym` is
// 'YYYY-MM'. Same offset trick as venueDayBounds, evaluated at both edges so
// a DST flip inside the month is handled.
export function venueMonthBounds(ym: string): { startUtc: string; endUtc: string } {
  const [y, m] = ym.split('-').map(Number);
  const tentative = new Date(Date.UTC(y!, m! - 1, 1, 0, 0, 0));
  const startUtcMs = tentative.getTime() - tzOffsetMinutes(tentative, VENUE_TZ) * 60000;
  const tentativeEnd = new Date(Date.UTC(y!, m!, 1, 0, 0, 0));
  const endUtcMs = tentativeEnd.getTime() - tzOffsetMinutes(tentativeEnd, VENUE_TZ) * 60000;
  return {
    startUtc: new Date(startUtcMs).toISOString(),
    endUtc: new Date(endUtcMs).toISOString(),
  };
}

export function formatVenueTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: VENUE_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatVenueDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatVenueDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    timeZone: VENUE_TZ,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
